import React from "react";
import { Link } from "react-router-dom";
import pluralize from "pluralize";

import { useRoutes } from "utils/routes";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import Grid from "ui/Grid";
import Tip from "ui/Tip";
import { DestinationLinks } from "features/Checks/AlertDestinationTag";

import { useAlertRuleListQuery } from "./gql/Query.generated";

function AlertRuleList({
  organizationSlug,
  serverId,
}: {
  organizationSlug: string;
  serverId?: string;
}) {
  const { organizationAlertRule, organizationAlertRuleNew } = useRoutes();
  const { data, loading, error } = useAlertRuleListQuery({
    variables: { organizationSlug, serverId },
  });
  if (loading || error || !data) {
    return <Loading error={!!error} />;
  }

  // "Applies to" sort key: org-wide rules (no server) group ahead of every
  // server (the "0" vs "1…" prefix), then servers sort by name.
  const rules = data.getAlertRules.map((rule) => ({
    ...rule,
    appliesTo: rule.server ? `1${rule.server.name}` : "0",
  }));

  return (
    <Panel
      title={
        <span className="inline-flex items-center">
          Alert Rules
          {serverId && (
            <Tip
              className="ml-1.5"
              content="Showing organization-wide alert rules plus rules that apply to this server."
            />
          )}
        </span>
      }
      secondaryTitle={
        <Link to={organizationAlertRuleNew(organizationSlug)}>
          New Alert Rule
        </Link>
      }
    >
      <Grid
        data={rules}
        striped
        defaultSortBy="name"
        columns={[
          {
            field: "name",
            header: "Name",
            width: "minmax(200px,1fr)",
            renderer: function NameCell({ rowData, fieldData }) {
              return (
                <Link to={organizationAlertRule(organizationSlug, rowData.id)}>
                  {fieldData}
                </Link>
              );
            },
          },
          {
            field: "appliesTo",
            header: "Applies to",
            width: "minmax(140px,1fr)",
            renderer: function ServerCell({ rowData }) {
              return <span>{rowData.server?.name ?? "All servers"}</span>;
            },
          },
          {
            field: "checkKinds",
            header: "Checks",
            width: "120px",
            disableSort: true,
            renderer: function ChecksCell({ rowData }) {
              return (
                <span>
                  {pluralize("check", rowData.checkKinds.length, true)}
                </span>
              );
            },
          },
          {
            field: "destinations",
            header: "Destinations",
            width: "minmax(200px,2fr)",
            disableSort: true,
            renderer: function DestinationsCell({ rowData }) {
              if (rowData.destinations.length === 0) {
                return <span className="text-[#606771]">—</span>;
              }
              return (
                <span className="text-sm">
                  <DestinationLinks
                    destinations={rowData.destinations}
                    organizationSlug={organizationSlug}
                  />
                </span>
              );
            },
          },
        ]}
      />
    </Panel>
  );
}

export default AlertRuleList;
